/**
 * Admin-web Logger Wrapper
 * 
 * Simple structured logging for the admin dashboard.
 * Uses the console with consistent formatting.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: unknown;
}

const formatLogEntry = (entry: LogEntry): string => {
  const { timestamp, level, message } = entry;
  return `[${timestamp}] [${level.toUpperCase()}] ${message}`;
};

class AdminLogger {
  private debugEnabled: boolean = false;

  setDebugEnabled(enabled: boolean): void {
    this.debugEnabled = enabled;
  }

  private log(level: LogLevel, message: string, data?: unknown): void {
    // Skip debug messages if debug is not enabled
    if (level === 'debug' && !this.debugEnabled) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
    };

    const formattedMessage = formatLogEntry(entry);

    switch (level) {
      case 'debug':
        if (data !== undefined) {
          console.debug(formattedMessage, data);
        } else {
          console.debug(formattedMessage);
        }
        break;
      case 'info':
        if (data !== undefined) {
          console.info(formattedMessage, data);
        } else {
          console.info(formattedMessage);
        }
        break;
      case 'warn':
        if (data !== undefined) {
          console.warn(formattedMessage, data);
        } else {
          console.warn(formattedMessage);
        }
        break;
      case 'error':
        if (data !== undefined) {
          console.error(formattedMessage, data);
        } else {
          console.error(formattedMessage);
        }
        break;
    }
  }

  debug(message: string, data?: unknown): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: unknown): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: unknown): void {
    this.log('warn', message, data);
  }

  error(message: string, data?: unknown): void {
    this.log('error', message, data);
  }

  /**
   * Create a child logger with a prefix
   */
  child(prefix: string): {
    debug: (message: string, data?: unknown) => void;
    info: (message: string, data?: unknown) => void;
    warn: (message: string, data?: unknown) => void;
    error: (message: string, data?: unknown) => void;
  } {
    return {
      debug: (message: string, data?: unknown) => this.debug(`[${prefix}] ${message}`, data),
      info: (message: string, data?: unknown) => this.info(`[${prefix}] ${message}`, data),
      warn: (message: string, data?: unknown) => this.warn(`[${prefix}] ${message}`, data),
      error: (message: string, data?: unknown) => this.error(`[${prefix}] ${message}`, data),
    };
  }
}

// Singleton instance
export const logger = new AdminLogger();

// Enable debug in development
if (import.meta.env.DEV) {
  logger.setDebugEnabled(true);
}

// Convenience child loggers for different parts of the app
export const authLogger = logger.child('Auth');
export const socketLogger = logger.child('Socket');
export const gameLogger = logger.child('Game');
